import { Box, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'

import ToothIcon from '@/assets/images/fi-rs-tooth.png'
import Chip from '@/components/Chip'
import PieChart from '@/components/PieChart'
import { useBreakpoints } from '@/hooks/useBreakpoints'
import { isNullorEmpty } from '@/utils'
import { pieChartSections } from '@/utils/constants'

type PrepAdvice = {
  prep_names_cervical?: string[]
  prep_ratios_cervical?: number[]
  prep_names_middle?: string[]
  prep_ratios_middle?: number[]
  prep_names_incisal?: string[]
  prep_ratios_incisal?: number[]
}

type AestheticModelEntity = {
  prep_advice: PrepAdvice
  natural_die_material: string
  recipeOutput: any
}

const PieChartCard = ({
  title,
  labels,
  values,
}: {
  title: string
  labels?: string[]
  values?: number[]
}) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        // minWidth: '225px',
        maxWidth: '300px',
        width: '31%',
        borderRadius: '16px',
        padding: 3,
        background: `linear-gradient(
      133.84deg,
      #4e4e4e -16.04%,
      #333333 9.33%,
      #1a1a1a 32.02%,
      #1a1a1a 62.06%,
      #262626 87.42%,
      #4e4e4e 112.12%
    )`,
        '@media (max-width: 1094px)': { minWidth: '350px' },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        mb={3}
      >
        <img src={ToothIcon} alt="error" />
        <Typography fontSize={'24px'} fontWeight={'bolder'} lineHeight={'20px'}>
          {title}
        </Typography>
      </Box>
      <Box
        flexGrow={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <PieChart
          labels={labels}
          values={values}
          height="100px"
          width="100px"
        />
      </Box>
    </Box>
  )
}

const AestheticModel = ({
  recipeOutput,
  prep_advice,
  natural_die_material,
}: AestheticModelEntity) => {
  const theme = useTheme()
  const isSingleDentin =
    recipeOutput?.provided_input?.recipe_type === 'single dentin'

  const updatedList = pieChartSections?.filter((item) =>
    isSingleDentin ? item?.key === 'middle' : item,
  )

  const { isDisplay1300 } = useBreakpoints()

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      px={4}
      sx={{ color: 'white' }}
    >
      <Box display="flex" alignItems="center" gap={1} paddingBottom={2}>
        <Typography
          sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: '20px' }}
        >
          Aesthetic Model:
        </Typography>
        {isNullorEmpty(natural_die_material) && (
          <Chip
            label={natural_die_material}
            sx={{
              bgcolor: theme.palette.background.chip,

              color: theme.palette.common.black,
              '&:hover': { bgcolor: theme.palette.background.chip },
            }}
            size="small"
          />
        )}
      </Box>

      <Stack
        direction="row"
        spacing={5}
        width="100%"
        sx={{
          justifyContent: 'center',
          ...(isDisplay1300
            ? { gap: '0', flexWrap: 'nowrap' }
            : { gap: '32px', flexWrap: 'wrap' }),
        }}
      >
        {updatedList?.map(({ key, label }) => (
          <React.Fragment key={key}>
            <PieChartCard
              title={label}
              labels={prep_advice[`prep_names_${key}` as keyof PrepAdvice]}
              values={prep_advice[`prep_ratios_${key}` as keyof PrepAdvice]}
            />
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  )
}

export default AestheticModel
